import axios, { AxiosError, AxiosResponse } from 'libs/axios';
import { Log } from 'types/log';
import { useMutation, useQuery } from 'react-query';
import { DEFAULT_PAGE_SIZE } from './usePagination';
import { Transformation } from 'types/transformation';
import {
    TRANSFORMATIONS_COUNT_QUERY_KEY,
    TRANSFORMATIONS_QUERY_KEY
} from './query-keys';
import { Dispatch, SetStateAction } from 'react';

export const useTransformations = ({
    currentPage = 1,
    setCurrentPage,
    enabled,
    pageSize = DEFAULT_PAGE_SIZE
}: {
    currentPage: number;
    setCurrentPage?: Dispatch<SetStateAction<number>>;
    enabled?: boolean;
    pageSize?: number;
}) => {
    return useQuery<Transformation[], AxiosError>(
        [TRANSFORMATIONS_QUERY_KEY, { currentPage, pageSize }],
        async () => {
            const { data } = await axios.get<Transformation[]>(
                '/api/ui/transformations',
                {
                    params: {
                        page: currentPage,
                        pageSize
                    }
                }
            );

            return data;
        },
        {
            keepPreviousData: true,
            enabled,
            onSuccess: (data) => {
                if (setCurrentPage && data.length === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                }
            }
        }
    );
};

export const useTransformationsCount = (enabled: boolean = true) => {
    return useQuery<number, AxiosError>(
        TRANSFORMATIONS_COUNT_QUERY_KEY,
        async () => {
            const { data } = await axios.get<number>(
                '/api/ui/transformations/count'
            );

            return data;
        },
        { enabled }
    );
};

export const useDeleteTransformations = () => {
    return useMutation<AxiosResponse, AxiosError, Transformation[]>(
        async (transformations) => {
            return axios.delete(
                `/api/ui/transformations?${transformations
                    .map((item) => `ids=${item.id}`)
                    .join('&')}`
            );
        }
    );
};

export const useAddTransformation = () => {
    return useMutation<AxiosResponse, AxiosError, Transformation>(
        async (data) => {
            return axios.put(`/api/ui/transformations`, data);
        }
    );
};

export const useEditTransformations = () => {
    return useMutation<AxiosResponse, AxiosError, Transformation>(
        async (data) => {
            return axios.post(`/api/ui/transformations/edit`, data);
        }
    );
};

interface ApplyTransformations {
    transformations: Transformation[];
    logs: Log[];
}

export const useApplyTransformations = () => {
    return useMutation<AxiosResponse<string>, AxiosError, ApplyTransformations>(
        async ({ transformations, logs }) => {
            const response = await axios.post(`/api/ui/transformations`, {
                transFormationIds: transformations.map((item) => item.id),
                logIds: logs.map((item) => item.id)
            });

            return response;
        }
    );
};

interface ApplyTransformationsToAll {
    transformations: Transformation[];
}

export const useApplyTransformationsToAll = () => {
    return useMutation<
        AxiosResponse<string>,
        AxiosError,
        ApplyTransformationsToAll
    >(async ({ transformations }) => {
        const response = await axios.post(
            `/api/ui/transformations/all`,
            transformations.map((item) => item.id)
        );

        return response;
    });
};
